import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import LinkButton from 'components/LinkButton/LinkButton';

// This component handles multiple input types (e.g. select and text), and input-specific logic is delegated to
// one of the following handler objects.

// Helper functions for rendering text inputs
const textHandler = {
  renderComponent: ({ entity, attribute, updateAttribute }) => (<input
    ref={input => input && input.focus()}
    type="text"
    defaultValue={entity[attribute]}
    onBlur={(e) => updateAttribute(e.target.value)}
    onKeyDown={e => {
      if (e.key === 'Enter') {
        updateAttribute(e.target.value)
      }
    }}
  />),

  renderLabel: ({ entity, attribute }) => entity[attribute],
};

// Helper functions for rendering selects
const selectHandler = {
  renderComponent: ({ entity, attribute, selectOptions, updateAttribute }) => {
    const i = selectOptions.findIndex(o => o.value === entity[attribute]);
    const defaultValue = i !== -1 ? i.toString() : null;
    const lookupValue = (value) => selectOptions[parseInt(value)].value;

    return (<select
      ref={input => input && input.focus()}
      defaultValue={defaultValue}
      onBlur={(e) => updateAttribute(lookupValue(e.target.value))}
      onChange={(e) => updateAttribute(lookupValue(e.target.value))}
    >
      {selectOptions.map((o, i) =>
        <option key={i} value={i.toString()}>{o.label}</option>
      )}
    </select>);
  },

  // Renders selected option from option list
  renderLabel: ({ entity, attribute, selectOptions }) => {
    const value = entity[attribute];
    if (value === null || value === undefined) {
      return value
    }  else {
      const option = selectOptions.find(o => o.value === value);
      return option ? option.label : null;
    }
  }
};

// Handler lookup by type
const typeHandlers = {
  text: textHandler,
  select: selectHandler,
};

// Renders an empty value
const renderEmpty = ({ emptyText, required }) => {
  if (required !== null) {
    return <i>{`${emptyText} (${required ? 'required' : 'optional'})`}</i>
  } else {
    return <i>{emptyText}</i>
  }
};

/**
 * A Component to provide pseudo-ContentEditable behavior for an entity attribute.
 *
 * It supports text and select controls as specified by `type`.
 *
 * @param {Object} props
 * @param {string} props.attribute
 * @param {Object} props.entity
 * @param {Function} [props.onBeginEdit]
 * @param {Function} props.onChange
 * @param {Function} props.component
 * @param {string} [props.className]
 * @param {boolean} [props.required=false]
 * @param {string} [props.emptyText=Click to add]
 * @param {string} props.type control type, one of "text" or "select"
 * @param {array} props.selectOptions array of select options
 * @return {JSX.Element}
 * @constructor
 */
const EditableAttribute = (props) => {
  const {
    attribute,
    entity,
    onBeginEdit,
    onChange,
    className,
    required,
    emptyText,
    type,
    selectOptions
  } = props;
  const [editingAttr, setEditingAttr] = useState(false);
  const updateAttribute = (newValue) => {
    setEditingAttr(false);
    if (newValue !== entity[attribute]) {
      onChange(attribute, newValue);
    }
  };

  const handler = typeHandlers[type];

  return (
    <span className={classNames('editable-attribute', className)}>
      {
        editingAttr
          ? handler.renderComponent({ entity, attribute, selectOptions, updateAttribute }) : (
            <LinkButton
              content={
                handler.renderLabel({ entity, attribute, selectOptions }) || renderEmpty({ emptyText, required })
              }
              onClick={(e) => {
                setEditingAttr(true);
                onBeginEdit(e, entity, attribute);
              }}
            />
          )
      }
    </span>
  );
};

EditableAttribute.propTypes = {
  attribute: PropTypes.string.isRequired,
  entity: PropTypes.objectOf(PropTypes.any).isRequired,
  onBeginEdit: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,

  /** Specify a boolean to indicate if input is required or not, or null to suppress message. */
  required: (props, propName, componentName) => {
    if (props[propName] !== null && typeof props[propName] !== 'boolean') {
      return new Error(`Invalid prop \`${propName}\` supplied to  \`${componentName}\`. Validation failed.`);
    }
  },

  emptyText: PropTypes.string,

  /** Input type. */
  type: PropTypes.oneOf(['text', 'select']),

  /** Options to use for type "select". */
  selectOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
  })),
};

EditableAttribute.defaultProps = {
  onBeginEdit: () => {},
  className: '',
  required: false,
  emptyText: 'Click to add',
  type: 'text',
  selectOptions: [],
};

export default EditableAttribute;
